/**
 * FormContainerControl
 * Adapter that bridges ControlRendererProps → FormContainer component props.
 * Renders the child forms of a form-container via FormRenderer, applying the configured data
 * binding strategy (shared-root / sub-root / independent).
 *
 * Child schemas and the values a child falls back to are owned by FormRenderer (see
 * utils/childFormLoader), so they are known even while this container is not mounted.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { FormContainer } from '@atlas-forms/controls-form-container-react';
import { useChildFormRegistryStore, childFormKey } from '@atlas-forms/form-engine-js';
import type { DataBindingConfig } from '@atlas-forms/controls-form-container-react';
import type { FormSchema } from '@atlas-forms/types-js';
import { FormRenderer } from '../../components/FormRenderer';
import { FormThemeProvider, useFormTheme } from '../../contexts/FormThemeContext';
import type { ControlRendererProps } from '../ControlRenderer';
import { resolveChildData, setAtPath } from '../../utils/childFormLoader';

// ─── Component ────────────────────────────────────────────────────────────────

interface ChildFormHostProps {
  formID: string;
  /** Registry scope of the container (its control id) */
  scope: string;
  schema: FormSchema;
  binding: DataBindingConfig;
  theme: React.ComponentProps<typeof FormThemeProvider>['theme'];
  isDisabled: boolean;
  getMaster: () => Record<string, unknown>;
  /** False once the container has unmounted; a child then must not keep its values */
  isAlive: () => boolean;
  onChange?: (value: any) => void;
}

/**
 * Renders one child form. It is registered with the container while mounted, keeps its values
 * when the step is left, and shows its own errors when the container asks (validationRequest).
 */
function ChildFormHost({ formID, scope, schema, binding, theme, isDisabled, getMaster, isAlive, onChange }: ChildFormHostProps) {
  const key = childFormKey(scope, formID);
  const validationRequest = useChildFormRegistryStore((s) => s.validationTicks[key] ?? 0);
  // Values kept from an earlier visit take precedence over the master data
  const initialValues = useChildFormRegistryStore.getState().getSnapshot(key) ?? resolveChildData(getMaster(), binding);

  return (
    <FormThemeProvider theme={theme}>
      <FormRenderer
        schema={schema}
        mode={isDisabled ? 'view' : 'edit'}
        nested
        initialValues={initialValues}
        validationRequest={validationRequest}
        onEngineReady={(childEngine) => useChildFormRegistryStore.getState().register(key, childEngine)}
        onEngineDispose={(childEngine) => {
          const registry = useChildFormRegistryStore.getState();
          if (registry.get(key) !== childEngine) return;
          // Keep what the user entered so it is there when the step is shown again
          if (isAlive()) registry.snapshot(key, childEngine.getValues());
          registry.unregister(key);
        }}
        onValuesChange={isDisabled ? undefined : (childValues) => {
          if (!onChange) return;
          const current = getMaster();
          if (binding.mode === 'sub-root') {
            if (binding.rootPath) onChange(setAtPath(current, binding.rootPath, childValues));
          } else if (binding.mode === 'independent') {
            // independent forms never write back to master
          } else {
            // shared-root: shallow merge into master
            onChange({ ...current, ...childValues });
          }
        }}
      />
    </FormThemeProvider>
  );
}

export const FormContainerControl: React.FC<ControlRendererProps> = ({
  control,
  value,
  onChange,
  disabled,
  mode,
}) => {
  const theme = useFormTheme();
  const config = control.config ?? {};

  // Determine if form is editable based on control properties and mode
  const isDisabled = disabled || control.disabled || control.readonly || mode === 'view';

  // Child schemas, loaded by FormRenderer for every container on the page
  const schemas = useChildFormRegistryStore((s) => s.schemas);

  // Always-current reference to master data — prevents stale closures in onSubmit
  const masterRef = useRef<Record<string, unknown>>({});
  const masterData: Record<string, unknown> =
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  masterRef.current = masterData;

  // Registry scope: the container's control id, so containers on one page never share child state
  const scope = control.id;

  // False after unmount. A child that disposes late checks it and keeps no values for a dead container
  const aliveRef = useRef(false);

  // On unmount, drop this container's live engines and kept values. Schemas and fallbacks stay:
  // FormRenderer owns them, and they are still needed to validate the children while unmounted.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      useChildFormRegistryStore.getState().releaseLive(scope);
    };
  }, [scope]);

  // Memoized renderChildForm to prevent FormContainer from re-rendering unnecessarily
  const memoizedRenderChildForm = useCallback(
    (formID: string, binding: DataBindingConfig) => {
      const schema = schemas.get(childFormKey(scope, formID));

      if (!schema) {
        return (
          <p style={{ color: '#9ca3af', fontSize: 13, margin: '16px 0' }}>
            Loading form {formID}…
          </p>
        );
      }

      // key by child form: each step mounts its own host, renderer and engine.
      return (
        <ChildFormHost
          key={formID}
          formID={formID}
          scope={scope}
          schema={schema}
          binding={binding}
          theme={theme}
          isDisabled={isDisabled}
          getMaster={() => masterRef.current}
          isAlive={() => aliveRef.current}
          onChange={onChange}
        />
      );
    },
    [schemas, scope, theme, isDisabled, onChange]
  );

  // Don't render if hidden (after all hooks, so the hook order never changes)
  if (control.hidden) {
    return null;
  }

  // In view mode, hide the container's own submit/nav buttons. The registry scope is passed in
  // config so the container's validation looks up the same child forms this control registers.
  const effectiveConfig = isDisabled
    ? { ...config, registryScope: scope, showSubmitButton: false, showNextButton: false, showPreviousButton: false }
    : { ...config, registryScope: scope };

  return (
    <FormThemeProvider theme={theme}>
      <FormContainer
        config={effectiveConfig as any}
        initialData={masterData}
        renderChildForm={memoizedRenderChildForm}
        theme={theme}
      />
    </FormThemeProvider>
  );
};

FormContainerControl.displayName = 'FormContainerControl';
